// Deterministic offline specimen profile - the D8 fallback when the JEV API is
// unreachable at hatch time. Same structured shape as a live JEV answer set
// (>=1 Choice, >=1 Score, >=1 Noul) so consumers never branch on the source.
// A pure function of the genome: keccak stream -> quantized probabilities.
import { AbiCoder, keccak256 } from 'ethers'

const coder = AbiCoder.defaultAbiCoder()

function genomeHash(genome) {
  return BigInt(
    keccak256(
      coder.encode(
        ['string', 'string', 'uint256', 'string', 'string', 'string'],
        [
          genome.name ?? '',
          genome.symbol ?? '',
          BigInt(genome.neuronId ?? 0),
          genome.region ?? '',
          genome.side ?? '',
          genome.flow ?? '',
        ],
      ),
    ),
  )
}

/** k deterministic draws from one 32-byte hash word (BE uint32 slices). */
function* draws(h, k) {
  let x = h
  for (let i = 0; i < k; i++) {
    x = BigInt(keccak256(coder.encode(['uint256'], [x])))
    yield Number(x & 0xffffffffn) / 2 ** 32
  }
}

/** Integer weights over n options summing to exactly 100, from one draw. */
function weights(draw, n) {
  const w = Array.from({ length: n }, () => 1)
  let left = 100 - n
  for (let i = 0; i < n; i++) {
    const bite = Math.min(left, Math.floor((((draw * 97 + i * 31) % 100) / 100) * left))
    w[i] += bite
    left -= bite
  }
  w[0] += left
  return w.map((v) => v / 100)
}

export function renderSpecimenTemplate(genome) {
  const h = genomeHash(genome)
  const d = draws(h, 4)
  const [wChoice, wScore, pNoul, wAlt] = d

  const habitat = ['canopy', 'understory', 'soil_litter']
  const choiceWeights = weights(wChoice, habitat.length)

  const legend = ['docile: stays near its tract', 'curious: explores new channels', 'fierce: fires at any charge']
  const scoreWeights = weights(wScore, legend.length)
  const score = scoreWeights.reduce((acc, p, i) => acc + p * i, 0)

  return {
    model: 'template-offline-v1',
    source: 'deterministic fallback (JEV unreachable at hatch time)',
    answers: {
      habitat: {
        type: 'choice',
        choice: habitat[choiceWeights.indexOf(Math.max(...choiceWeights))],
        probabilities: Object.fromEntries(habitat.map((k, i) => [k, choiceWeights[i]])),
        confidence: 0.5 + wAlt / 4,
      },
      temperament: {
        type: 'score',
        score,
        legend,
        probabilities: Object.fromEntries(legend.map((_, i) => [String(i), scoreWeights[i]])),
        confidence: 0.5 + pNoul / 4,
      },
      light_averse: {
        type: 'noul',
        noul: pNoul,
      },
    },
    usage: { input_tokens: 0, output_tokens: 0 },
  }
}
